import { useMemo, useState } from 'react';
import { InlineStack, Select } from '@customerio/pluma-components/react';
import { fieldError } from '@customerio/dryad';
import { useEnvironmentOptional } from '@/services/db/environment/queries';
import { isEmptyString } from '@/utils/string';
import { useBuilderActions, useLeafState, useMeta, useNodeErrors } from '@customerio/dryad-react';
import type { ReactNode } from 'react';
import type { OptOutConditionState, OptOutNegateWording } from './opt-out-types';
import {
  getAvailableOptOutChannels,
  getOptOutIdentities,
  normalizeOptOutChannel,
  optOutChannelOptions,
} from './opt-out-channel';

type OptOutRowProps = {
  /** The leaf node's id, supplied by the group to every registered view. */
  id: string;
  /** The plugin type gating the typed read — the surface's opt-out leaf. */
  conditionType: string;
  /** The wording for the negate select, which differs per surface. */
  negateWording: OptOutNegateWording;
  /** The condition-type select, where the surface offers more than one type. */
  typeSelect?: ReactNode;
};

/**
 * An opt-out condition row: [has (not) opted out of] [channel] [phone number].
 * The channel picker only renders when the workspace has senders on more than
 * one channel; with a single channel the channel is implicit.
 */
export function OptOutRow({ id, conditionType, negateWording, typeSelect }: OptOutRowProps) {
  const meta = useMeta();
  const { state, node } = useLeafState<OptOutConditionState>(id, conditionType);
  const { updateState } = useBuilderActions();
  const errors = useNodeErrors(id);
  const environment = useEnvironmentOptional();
  const identities = useMemo(() => environment.data?.identities ?? [], [environment.data]);

  // "Phone number can't be blank." renders only once the picker has been
  // visited (closing its dropdown) — or from the start when the row mounted
  // with a number already chosen — so a freshly added row doesn't open
  // covered in red.
  const [phoneNumberTouched, setPhoneNumberTouched] = useState(() => state.phoneNumber != null);
  const phoneNumberErrorMessage = phoneNumberTouched ? fieldError(errors, 'phoneNumber') : undefined;

  const negateOptions = [
    { value: 'false', label: negateWording.optedOut },
    { value: 'true', label: negateWording.notOptedOut },
  ];

  const showChannelSelect = getAvailableOptOutChannels(identities).length > 1;

  const phoneNumberOptions = useMemo(() => {
    const identityOptions = getOptOutIdentities(identities, state.channel).map((identity) => ({
      label: isEmptyString(identity.name) ? identity.phone : identity.name,
      value: identity.phone,
    }));
    return [{ label: 'Any number', value: '' }, ...identityOptions];
  }, [identities, state.channel]);

  return (
    <InlineStack data-testid="dryad-condition-row" gap="050" alignBlock="baseline" wrap>
      {typeSelect}
      <Select
        data-testid="dryad-negate-select"
        ariaLabel="Opt-out negate"
        size="sm"
        options={negateOptions}
        value={state.negate ? 'true' : 'false'}
        onChange={(value: string | null) => {
          if (value != null && node != null) {
            updateState(node, { negate: value === 'true' });
          }
        }}
        isDisabled={meta.isDisabled}
      />
      {showChannelSelect ? (
        <Select
          data-testid="dryad-opt-out-channel-select"
          ariaLabel="Opt-out channel"
          size="sm"
          options={optOutChannelOptions(identities)}
          value={state.channel}
          onChange={(value: string | null) => {
            if (value == null || node == null) {
              return;
            }
            // Senders differ per channel, so clear the chosen number when
            // switching. The cleared picker is a fresh choice, so it isn't
            // flagged red until visited again.
            updateState(node, { channel: normalizeOptOutChannel(value), phoneNumber: null });
            setPhoneNumberTouched(false);
          }}
          isDisabled={meta.isDisabled}
        />
      ) : null}
      <Select
        data-testid="dryad-opt-out-phone-select"
        ariaLabel="Phone number"
        size="sm"
        options={phoneNumberOptions}
        value={state.phoneNumber}
        placeholder="Select a number"
        error={phoneNumberErrorMessage}
        onChange={(value: string | null) => {
          if (value != null && node != null) {
            updateState(node, { phoneNumber: value });
          }
        }}
        onOpenChange={(isOpen: boolean) => {
          if (!isOpen) {
            setPhoneNumberTouched(true);
          }
        }}
        isDisabled={meta.isDisabled}
      />
    </InlineStack>
  );
}
